import { useState } from 'react';
import { Button } from '../Default Button/Button';
import { IconLibrary } from '../../../display/IconLibrary/IconLibrary';
import { IconName } from '../../../display/IconLibrary/IconLibraryTypes';
import { FlexContainer } from '../../../containers/ContentContainers/FlexContainer';
import { Box, Typography, useTheme } from '@mui/material';
import ConfettiExplosion from 'react-confetti-explosion';
import { useDisplayModeContext } from '../../../theme/display';
import { motion } from 'framer-motion';

export interface PizzazzButtonProps {
  text: string;
  onClick: () => void;
  iconName?: IconName;
  isExplosive?: boolean;
}

export const PizzazzButton = ({
  text,
  iconName = 'Confetti',
  onClick,
  isExplosive = true,
}: PizzazzButtonProps) => {
  const theme = useTheme();
  const { displayMode, isAnimating } = useDisplayModeContext();
  const isCompact = displayMode === 'compact';
  const [exploding, setExploding] = useState(false);

  const labelVariants = {
    hidden: { opacity: 0, transition: { duration: 0.2 } },
    visible: { opacity: 1, transition: { duration: 0.2, delay: 0.5 } },
  };

  const widthVariants = {
    hidden: { width: 0, transition: { duration: 0.2 } },
    visible: { width: 'auto', transition: { duration: 0.2, delay: 0.5 } },
  };

  function handleClick() {
    if (isExplosive) {
      setExploding(true);
      setTimeout(() => {
        setExploding(false);
      }, 3000);
    }
    onClick();
  }

  return (
    <FlexContainer
      direction="column"
      alignItems="center"
      justifyContent="center"
      height="fit-content"
      width="100%"
      margin="0 auto"
      sx={{
        position: 'relative',
      }}
    >
      <Button
        variant="contained"
        onClick={handleClick}
        sx={{
          background:
            'linear-gradient(75deg, #81CDC3 0%, #021E38 25%, #021E38 50%, #FF7B65 85%, #FFEDDA 100%)',
          border: `3px solid ${theme.palette.primary.main}`,
        }}
      >
        <FlexContainer
          direction="row"
          gap={isCompact ? 0 : 1}
          alignItems="center"
        >
          <IconLibrary iconName={iconName} weight="bold" />
          <motion.div
            variants={widthVariants}
            animate={isCompact && !isAnimating ? 'hidden' : 'visible'}
            style={{
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <motion.div
              variants={labelVariants}
              animate={isCompact && !isAnimating ? 'hidden' : 'visible'}
              style={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Typography variant="bodyMedium" fontWeight={600}>
                {text}
              </Typography>
            </motion.div>
          </motion.div>
        </FlexContainer>
      </Button>
      {exploding && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',
            zIndex: 1000,
          }}
        >
          <ConfettiExplosion
            force={0.8}
            duration={3000}
            particleCount={250}
            width={1600}
          />
        </Box>
      )}
    </FlexContainer>
  );
};
